import React from "react";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BsPlusCircle,
  BsChevronLeft,
  BsChevronRight,
  BsSortUp,
  BsSortDown,
} from "react-icons/bs";
import { Oval } from "react-loader-spinner";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  FleetOptimization,
  FleetOptimizationStatus,
  runIsInfeasible,
  planInfeasibilityDetails,
} from "../../../models";
import { listRuns } from "../fleetOptimizationSlice";
import { getDeliveries } from "../../Deliveries/deliveriesSlice";
import { getPickups } from "../../Pickups/pickUpsSlice";
import { getDriversV2 } from "../../DriversV2/driversV2Slice";
import { stopLabel, COMPACTNESS_STOPS, DUMP_FEE_WEIGHT_STOPS } from "../NewRunConfig/PenaltySlider";

import "./RunsList.scss";

function statusBadgeClass(status: FleetOptimizationStatus): string {
  switch (status) {
    case "pending":
      return "badge-pending";
    case "running":
      return "badge-running";
    case "complete":
      return "badge-complete";
    case "failed":
      return "badge-failed";
    default:
      return "badge-pending";
  }
}

const PAGE_SIZE = 10;

// Total cost = the best (first) option's full objective: operating + maintenance +
// soft penalties (i.e. the solver's cost).
function totalCost(run: FleetOptimization): string {
  // An infeasible best option's objective is the ~1e9 marker, not a real cost — hide it.
  if (runIsInfeasible(run)) return "—";
  const cost = run.result?.options?.[0]?.cost;
  if (cost === undefined || cost === null) return "—";
  return `$${cost.toFixed(2)}`;
}

// Operational cost = real cash outlay of the best option: fuel + wage + dump fees.
// Maintenance is a notional per-mile wear estimate, grouped under penalties instead.
function operationalCost(run: FleetOptimization): string {
  const o = run.result?.options?.[0];
  if (!o) return "—";
  return `$${(o.fuelCost + o.wageCost + o.dumpFees).toFixed(2)}`;
}

// Not-billed total of the best option: soft penalties + notional maintenance.
function penaltiesCost(run: FleetOptimization): string {
  const o = run.result?.options?.[0];
  if (!o) return "—";
  const total =
    (o.penalties ?? []).reduce((s, x) => s + x.amount, 0) + (o.maintenanceCost ?? 0);
  if (total === 0) return "—";
  return `$${total.toFixed(2)}`;
}

// Best option's not-billed chips: maintenance first, then each soft penalty by type.
function penaltyChips(run: FleetOptimization): { type: string; amount: number }[] {
  const o = run.result?.options?.[0];
  if (!o) return [];
  const chips: { type: string; amount: number }[] = [];
  if (o.maintenanceCost && o.maintenanceCost > 0) {
    chips.push({ type: "maintenance", amount: o.maintenanceCost });
  }
  return chips.concat(o.penalties ?? []);
}

// Best option's total miles (meters→mi).
function totalMiles(run: FleetOptimization): string {
  const m = run.result?.options?.[0]?.meters;
  if (m === undefined || m === null) return "—";
  return (m / 1609.34).toFixed(1);
}

// Route time of the best option as "drive / total" hours (e.g. "1.2 / 8.5h"), or "—".
function routeTime(run: FleetOptimization): string {
  const o = run.result?.options?.[0];
  if (!o) return "—";
  return `${(o.drivingSeconds / 3600).toFixed(1)} / ${(o.totalSeconds / 3600).toFixed(1)}h`;
}

// Deliveries + pickups serviced (from the first option; same jobs across options).
function stopCount(run: FleetOptimization): string {
  const routes = run.result?.options?.[0]?.routes;
  if (!routes) return "—";
  const n = routes.reduce(
    (sum, r) =>
      sum +
      r.stops.filter((s) => s.type === "delivery" || s.type === "pickup").length,
    0
  );
  return String(n);
}

// The territory-tightness knob rendered as its intensity label (Loose … Max).
// Absent on a run → the backend default of 0, i.e. "Loose".
function territoryLabel(run: FleetOptimization): string {
  return stopLabel(run.input.compactnessPenalty ?? 0, COMPACTNESS_STOPS);
}

// Dump-fee rank weight rendered as a percentage.
// Absent on a run → the backend default of 1.0 (100%).
function dumpFeeWeightLabel(run: FleetOptimization): string {
  const w = run.input.dumpFeeRankWeight ?? 1.0;
  return `${Math.round(w * 100)}%`;
}

// A duration in ms as "6min 32s" / "45s" / "820ms".
export function humanDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s ? `${m}min ${s}s` : `${m}min`;
}

// Solve wall-clock of the run, human-friendly (or "—" when unavailable).
function solveDuration(run: FleetOptimization): string {
  const ms = run.result?.solve?.durationMs;
  if (ms == null || ms <= 0) return "—";
  return humanDuration(ms);
}

// ---- Column sorting --------------------------------------------------------
// Sorting runs on the *loaded page* only (the list is server-paginated, so the
// Redux `runs` array holds at most one page). For a typical day's handful of
// runs that is the whole set; a day with >10 runs sorts within the page.

export type RunSortField =
  | "time"
  | "drivers"
  | "stops"
  | "operationalCost"
  | "miles"
  | "routeTime"
  | "penalties"
  | "totalCost"
  | "solved"
  | "status";

export type SortDirection = "asc" | "desc";

// Raw sortable value for a run under a given field. Returns null for "missing"
// (no result yet, or an infeasible run's bogus ~1e9 cost) so those rows can be
// forced to the bottom regardless of direction — mirroring the "—" the cell
// renders. Numbers sort numerically; the status string sorts lexically.
export function runSortValue(
  run: FleetOptimization,
  field: RunSortField
): number | string | null {
  const o = run.result?.options?.[0];
  switch (field) {
    case "time":
      return Date.parse(run.createdAt) || null;
    case "drivers":
      return run.input.driverAssignments.length;
    case "stops": {
      const routes = o?.routes;
      if (!routes) return null;
      return routes.reduce(
        (sum, r) =>
          sum +
          r.stops.filter((s) => s.type === "delivery" || s.type === "pickup")
            .length,
        0
      );
    }
    case "operationalCost":
      return o ? o.fuelCost + o.wageCost + o.dumpFees : null;
    case "miles":
      return o?.meters ?? null;
    case "routeTime":
      return o?.totalSeconds ?? null;
    case "penalties":
      return o
        ? (o.penalties ?? []).reduce((s, x) => s + x.amount, 0) +
            (o.maintenanceCost ?? 0)
        : null;
    case "totalCost":
      if (runIsInfeasible(run)) return null;
      return o?.cost ?? null;
    case "solved": {
      const ms = run.result?.solve?.durationMs;
      return ms == null || ms <= 0 ? null : ms;
    }
    case "status":
      return runIsInfeasible(run) ? "infeasible" : run.status;
  }
}

// Order two runs by a field + direction. Missing values (null) always sort to
// the bottom in both directions, so "—" rows never crowd the top.
export function compareRuns(
  a: FleetOptimization,
  b: FleetOptimization,
  field: RunSortField,
  dir: SortDirection
): number {
  const va = runSortValue(a, field);
  const vb = runSortValue(b, field);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  let cmp: number;
  if (typeof va === "string" || typeof vb === "string") {
    cmp = String(va).localeCompare(String(vb));
  } else {
    cmp = va - vb;
  }
  return dir === "asc" ? cmp : -cmp;
}

// Return a sorted copy of `runs`, or the original array when no sort is active
// (preserving the backend's order on first load).
export function sortRuns(
  runs: FleetOptimization[],
  sort: { field: RunSortField; dir: SortDirection } | null
): FleetOptimization[] {
  if (!sort) return runs;
  return [...runs].sort((a, b) => compareRuns(a, b, sort.field, sort.dir));
}

export function RunsList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dateParam = searchParams.get("date");
  const date = dateParam || moment().format("YYYY-MM-DD");

  // Prev/next-day arrows just rewrite the ?date= param; the date-driven effects
  // below pick up the change and refetch. replace:true keeps day-stepping out of
  // the browser history stack. Matches the Schedule screen's day navigation.
  const shiftDay = (deltaDays: number) => {
    const next = moment(date).add(deltaDays, "day").format("YYYY-MM-DD");
    const params = new URLSearchParams(searchParams);
    params.set("date", next);
    setSearchParams(params, { replace: true });
  };

  const runs = useAppSelector((state) => state.fleetOptimization.runs);
  const listAPI = useAppSelector((state) => state.fleetOptimization.listAPI);

  // Name resolution for the pin tooltip — drivers are global; deliveries/pickups
  // are keyed by the list's date. Optional-chained so component tests that register
  // only the fleetOptimization slice don't crash on a missing slice.
  const drivers = useAppSelector((s) => s.driversV2?.drivers) || [];
  const allDeliveries = useAppSelector((s) => s.deliveries?.deliveries) || [];
  const allPickups = useAppSelector((s) => s.pickups?.pickups) || [];

  const driverName = (id: string) => {
    const d = drivers.find((x: any) => x.userId === id || x.id === id);
    return d?.name ?? `Driver #${id}`;
  };
  const jobName = (id: string) => {
    const d = allDeliveries.find((x: any) => x.id === id);
    if (d) return d.name;
    const p = allPickups.find((x: any) => x.id === id);
    if (p) return p.name;
    return `#${id}`;
  };

  const [page, setPage] = React.useState(0);

  // Column sort for the loaded page; null = backend order (initial load).
  const [sort, setSort] = React.useState<{
    field: RunSortField;
    dir: SortDirection;
  } | null>(null);

  // Click a header: toggle direction if it's already the sort column, else
  // start sorting that column ascending.
  const onSort = (field: RunSortField) =>
    setSort((cur) =>
      cur?.field === field
        ? { field, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" }
    );

  const sortedRuns = React.useMemo(() => sortRuns(runs, sort), [runs, sort]);

  // A clickable, sortable column header: shows an up/down caret on the active
  // column. Non-sortable columns (Settings, actions) stay plain <th>.
  const sortableHeader = (label: string, field: RunSortField) => {
    const active = sort?.field === field;
    return (
      <th
        className={`sortable-th${active ? " is-sorted" : ""}`}
        onClick={() => onSort(field)}
        role="button"
        aria-label={`Sort by ${label}`}
      >
        <span className="header-cell">
          {label}
          {active &&
            (sort!.dir === "asc" ? (
              <BsSortUp className="sorting-icon" />
            ) : (
              <BsSortDown className="sorting-icon" />
            ))}
        </span>
      </th>
    );
  };

  // Reset to the first page whenever the date changes.
  React.useEffect(() => {
    setPage(0);
  }, [date]);

  React.useEffect(() => {
    dispatch(listRuns({ date, limit: PAGE_SIZE, offset: page * PAGE_SIZE }));
  }, [dispatch, date, page]);

  // Back the pin tooltip's name resolution: this date's jobs + the global drivers.
  React.useEffect(() => {
    dispatch(getDeliveries(date));
    dispatch(getPickups(date));
    dispatch(getDriversV2());
  }, [dispatch, date]);

  // No total from the API — a full page implies there may be another.
  const hasMore = runs.length === PAGE_SIZE;

  const onNewRun = () => navigate(`/fleet-optimization/new?date=${date}`);

  const onRowClick = (run: FleetOptimization) => navigate(`/fleet-optimization/${run.id}`);

  // Clone a past run's inputs into a New Run form (without opening the run).
  const onCopyInputs = (run: FleetOptimization) =>
    navigate(`/fleet-optimization/new?date=${run.input.date}`, {
      state: { prefill: run.input },
    });

  if (listAPI.loading && runs.length === 0) {
    return (
      <div className="runs-list-component">
        <div className="initial-loading">
          <Oval color="#131313" height={40} width={40} secondaryColor="#FBD20A" />
        </div>
      </div>
    );
  }

  if (listAPI.error && !listAPI.value) {
    return (
      <div className="runs-list-component">
        <div className="error-state">Failed to load runs. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="runs-list-component">
      <div className="controllers-container">
        <div className="title-container">
          <div className="title-text">Fleet Optimization</div>
          <div className="date-nav">
            <div
              className="icon-container"
              onClick={() => shiftDay(-1)}
              role="button"
              aria-label="Previous day"
            >
              <BsChevronLeft className="icon" />
            </div>
            <div
              className="icon-container"
              onClick={() => shiftDay(1)}
              role="button"
              aria-label="Next day"
            >
              <BsChevronRight className="icon" />
            </div>
            <div className="title-date">{moment(date).format("MMM D, YYYY")}</div>
          </div>
        </div>
        <div
          className="new-run-button"
          onClick={onNewRun}
          role="button"
          aria-label="New run"
        >
          <BsPlusCircle className="icon" />
          New run
        </div>
      </div>

      <div className="table-container">
        {runs.length === 0 ? (
          <div className="empty-state">No runs for this date.</div>
        ) : (
          <table className="new-table runs-table">
            <thead>
              <tr>
                {sortableHeader("Time", "time")}
                {sortableHeader("Drivers", "drivers")}
                {sortableHeader("Stops", "stops")}
                <th>Settings</th>
                {sortableHeader("Operational cost", "operationalCost")}
                {sortableHeader("Miles", "miles")}
                {sortableHeader("Route time (drive/total)", "routeTime")}
                {sortableHeader("Penalties (incl. maintenance)", "penalties")}
                {sortableHeader("Total cost", "totalCost")}
                {sortableHeader("Solved", "solved")}
                {sortableHeader("Status", "status")}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRuns.map((run) => (
                <tr
                  key={run.id}
                  onClick={() => onRowClick(run)}
                  className="clickable-row"
                >
                  <td>{moment(run.createdAt).format("h:mm a")}</td>
                  <td>{run.input.driverAssignments.length}</td>
                  <td>
                    <span className="stops-cell">
                      {stopCount(run)}
                      {(run.input.locks?.length ?? 0) > 0 && (
                        <span className="pin-badge">
                          📌 {run.input.locks!.length}
                          <span className="pin-tip">
                            <span className="pin-tip-title">Pinned stops</span>
                            {run.input.locks!.map((l, i) => (
                              <span className="pin-tip-row" key={i}>
                                <span className="pin-tip-stop">
                                  {jobName(l.jobId)}
                                </span>
                                <span className="pin-tip-driver">
                                  {driverName(l.driverId)}
                                </span>
                              </span>
                            ))}
                          </span>
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="settings-cell">
                    <span
                      className={`territory-chip${
                        territoryLabel(run) === "Loose" ? " is-loose" : ""
                      }`}
                    >
                      Territory: {territoryLabel(run)}
                    </span>
                    <span
                      className={`territory-chip${
                        dumpFeeWeightLabel(run) === "100%" ? " is-loose" : ""
                      }`}
                    >
                      Dump-fee weight: {dumpFeeWeightLabel(run)}
                    </span>
                  </td>
                  <td>{operationalCost(run)}</td>
                  <td>{totalMiles(run)}</td>
                  <td>{routeTime(run)}</td>
                  <td className="penalties-cell">
                    {penaltiesCost(run)}
                    {penaltyChips(run).length > 0 && (
                      <span className="penalties-breakdown">
                        {penaltyChips(run).map((p, i) => (
                          <span key={i} className="penalty-chip">
                            {p.type.replace(/_/g, " ")} ${p.amount.toFixed(2)}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>{totalCost(run)}</td>
                  <td>{solveDuration(run)}</td>
                  <td>
                    {runIsInfeasible(run) ? (
                      <span
                        className="status-badge badge-failed"
                        title={planInfeasibilityDetails(run.result?.options?.[0]).join("\n")}
                      >
                        infeasible
                      </span>
                    ) : (
                      <span className={`status-badge ${statusBadgeClass(run.status)}`}>
                        {run.status}
                      </span>
                    )}
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="copy-inputs-button"
                      title="Open New Run pre-filled with this run's inputs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyInputs(run);
                      }}
                    >
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(page > 0 || hasMore) && (
        <div className="pagination-controls">
          <button
            type="button"
            className="page-button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span className="page-indicator">Page {page + 1}</span>
          <button
            type="button"
            className="page-button"
            disabled={!hasMore}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
